import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { kycService } from "../services/kycService";
import {
  FaTimes,
  FaCheck,
  FaTimes as FaReject,
  FaClock,
  FaFileAlt,
  FaUser,
  FaCalendar,
  FaMapMarkerAlt,
  FaIdCard,
} from "react-icons/fa";
import { getImageUrl } from "../utils/docS3";

interface KYCHistoryModalProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

interface KYCHistoryItem {
  id: string;
  status: string;
  submitted_at: string;
  verified_at?: string;
  rejected_at?: string;
  rejection_reason?: string;
  rejection_notes?: string;
  verified_by?: string;
  rejected_by?: string;
  kyc_data: {
    full_name: string;
    date_of_birth: string;
    pan_number: string;
    aadhaar_number: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    occupation: string;
    annual_income: string;
  };
  documents: Array<{
    document_type: string;
    file_url: string;
    file_name: string;
    file_size: number;
    mime_type: string;
  }>;
}

interface KYCHistoryData {
  user_id: string;
  user_email: string;
  user_name: string;
  current_kyc_status: string;
  kyc_history: KYCHistoryItem[];
  total_submissions: number;
}

export default function KYCHistoryModal({
  userId,
  userName,
  onClose,
}: KYCHistoryModalProps) {
  const [historyData, setHistoryData] = useState<KYCHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] =
    useState<KYCHistoryItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [docUrls, setDocUrls] = useState<{ [key: string]: string }>({});
  useEffect(() => {
    fetchKYCHistory();
  }, [userId]);

  const fetchKYCHistory = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await kycService.getKYCHistory(userId);
      setHistoryData(data);
    } catch (err: any) {
      if (err.message && err.message.includes("404")) {
        setError("No KYC history found for this user.");
      } else {
        setError("Failed to fetch KYC history.");
      }
      setHistoryData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveKYC = async () => {
    if (!selectedSubmission || selectedSubmission.status !== "pending") {
      return;
    }

    setActionLoading(true);
    try {
      await kycService.approveKYC(userId);
      // Refresh the history after approval
      await fetchKYCHistory();
      // Select the updated submission
      const updatedSubmission = historyData?.kyc_history.find(
        (s) => s.id === selectedSubmission.id
      );
      if (updatedSubmission) {
        setSelectedSubmission(updatedSubmission);
      }
    } catch (err: any) {
      setError("Failed to approve KYC: " + (err.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };
  useEffect(() => {
    const fetchDocUrls = async () => {
      if (!selectedSubmission) return;
  
      const urls: { [key: string]: string } = {};
      for (const doc of selectedSubmission.documents) {
        try {
          // remove leading slash
          const cleanKey = doc.file_url.startsWith("/") 
            ? doc.file_url.slice(1) 
            : doc.file_url;
  
          const signedUrl = await getImageUrl(cleanKey, null, "");
          urls[doc.file_url] = signedUrl;
        } catch (err) {
          console.error("Error generating signed URL for:", doc.file_url, err);
        }
      }
      setDocUrls(urls);
    };
  
    fetchDocUrls();
  }, [selectedSubmission]);
  const handleRejectKYC = async () => {
    if (
      !selectedSubmission ||
      selectedSubmission.status !== "pending" ||
      !rejectionReason.trim()
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await kycService.rejectKYC(userId, rejectionReason);
      // Refresh the history after rejection
      await fetchKYCHistory();
      // Select the updated submission
      const updatedSubmission = historyData?.kyc_history.find(
        (s) => s.id === selectedSubmission.id
      );
      if (updatedSubmission) {
        setSelectedSubmission(updatedSubmission);
      }
      setShowRejectionModal(false);
      setRejectionReason("");
    } catch (err: any) {
      setError("Failed to reject KYC: " + (err.message || "Unknown error"));
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectionModal = () => {
    setShowRejectionModal(true);
    setRejectionReason("");
  };

  const closeRejectionModal = () => {
    setShowRejectionModal(false);
    setRejectionReason("");
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "verified":
        return <FaCheck className="text-green-500" />;
      case "rejected":
        return <FaReject className="text-red-500" />;
      case "pending":
        return <FaClock className="text-yellow-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "verified":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getDocumentTypeName = (type: string) => {
    const typeMap: { [key: string]: string } = {
      pan_card: "PAN Card",
      aadhaar_card: "Aadhaar Card",
      address_proof: "Address Proof",
      income_proof: "Income Proof",
      photograph: "Photograph",
    };
    return typeMap[type] || type.replace("_", " ").toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatIncome = (income: string) => {
    const incomeMap: { [key: string]: string } = {
      "below_2.5l": "Below ₹2.5 Lakhs",
      "2.5l_5l": "₹2.5 - 5 Lakhs",
      "5l_10l": "₹5 - 10 Lakhs",
      "10l_25l": "₹10 - 25 Lakhs",
      "25l_50l": "₹25 - 50 Lakhs",
      above_50l: "Above ₹50 Lakhs",
    };
    return incomeMap[income] || income;
  };

  const formatOccupation = (occupation: string) => {
    const occupationMap: { [key: string]: string } = {
      salaried: "Salaried",
      self_employed: "Self Employed",
      business_owner: "Business Owner",
      student: "Student",
      retired: "Retired",
      other: "Other",
    };
    return occupationMap[occupation] || occupation;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">KYC History - {userName}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes />
            </button>
          </div>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading KYC history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">KYC History - {userName}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes />
            </button>
          </div>
          <div className="text-center py-8">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchKYCHistory}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold">KYC History</h2>
            <p className="text-gray-600">{userName}</p>
            <p className="text-sm text-gray-500">
              Total Submissions: {historyData?.total_submissions || 0}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {!historyData || historyData.kyc_history.length === 0 ? (
          <div className="text-center py-8">
            <FaFileAlt className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-600">
              No KYC submissions found for this user.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* KYC History List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Submission History</h3>
              {historyData.kyc_history.map((submission, index) => (
                <div
                  key={submission.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedSubmission?.id === submission.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedSubmission(submission)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(submission.status)}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          submission.status
                        )}`}
                      >
                        {submission.status.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      #{historyData.kyc_history.length - index}
                    </span>
                  </div>

                  <div className="text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-1">
                      <FaCalendar className="text-gray-400" />
                      Submitted: {formatDate(submission.submitted_at)}
                    </div>
                  </div>

                  {submission.verified_at && (
                    <div className="text-sm text-green-600 mb-1">
                      <div className="flex items-center gap-1">
                        <FaCheck />
                        Verified: {formatDate(submission.verified_at)}
                        {submission.verified_by &&
                          ` by ${submission.verified_by}`}
                      </div>
                    </div>
                  )}

                  {submission.rejected_at && (
                    <div className="text-sm text-red-600 mb-1">
                      <div className="flex items-center gap-1">
                        <FaReject />
                        Rejected: {formatDate(submission.rejected_at)}
                        {submission.rejected_by &&
                          ` by ${submission.rejected_by}`}
                      </div>
                    </div>
                  )}

                  {submission.rejection_reason && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded mt-2">
                      <strong>Reason:</strong> {submission.rejection_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* KYC Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Submission Details</h3>
              {selectedSubmission ? (
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FaUser />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Full Name:</span>
                        <p className="font-medium">
                          {selectedSubmission.kyc_data.full_name}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Date of Birth:</span>
                        <p className="font-medium">
                          {selectedSubmission.kyc_data.date_of_birth}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">PAN Number:</span>
                        <p className="font-medium">
                          {selectedSubmission.kyc_data.pan_number}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Aadhaar Number:</span>
                        <p className="font-medium">
                          {selectedSubmission.kyc_data.aadhaar_number}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FaMapMarkerAlt />
                      Address Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Address:</span>
                        <p className="font-medium">
                          {selectedSubmission.kyc_data.address}
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <span className="text-gray-600">City:</span>
                          <p className="font-medium">
                            {selectedSubmission.kyc_data.city}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">State:</span>
                          <p className="font-medium">
                            {selectedSubmission.kyc_data.state}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">PIN Code:</span>
                          <p className="font-medium">
                            {selectedSubmission.kyc_data.pincode}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FaIdCard />
                      Financial Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Occupation:</span>
                        <p className="font-medium">
                          {formatOccupation(
                            selectedSubmission.kyc_data.occupation
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Annual Income:</span>
                        <p className="font-medium">
                          {formatIncome(
                            selectedSubmission.kyc_data.annual_income
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FaFileAlt />
                      Documents ({selectedSubmission.documents.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedSubmission.documents.map((doc, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded"
                        >
                          <div className="flex items-center gap-2">
                            <FaFileAlt className="text-gray-400" />
                            <span className="text-sm font-medium">
                              {getDocumentTypeName(doc.document_type)}
                            </span>
                          </div>
                          <a
                            href={docUrls[doc.file_url] || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons for Pending Submissions */}
                  {selectedSubmission.status === "pending" && (
                    <div className="border rounded-lg p-4 bg-yellow-50">
                      <h4 className="font-semibold mb-3 text-yellow-800">
                        KYC Actions
                      </h4>
                      <div className="flex gap-3">
                        <button
                          onClick={handleApproveKYC}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FaCheck />
                          {actionLoading ? "Approving..." : "Approve KYC"}
                        </button>
                        <button
                          onClick={openRejectionModal}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FaReject />
                          {actionLoading ? "Rejecting..." : "Reject KYC"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaFileAlt className="text-4xl mx-auto mb-4 text-gray-300" />
                  <p>Select a submission to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {showRejectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Reject KYC</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={4}
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={closeRejectionModal}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectKYC}
                  disabled={actionLoading || !rejectionReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? "Rejecting..." : "Reject KYC"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
